<script setup lang="ts">
import type { components as StrapiComponents } from '@core/types/clients/strapi'
import type { StrapiBlock } from '@nimbus/types/components/StrapiBlockRenderer'
import { computed } from '#imports'

const props = defineProps<{
    centeredText: StrapiComponents['schemas']['DynamicCenteredTextComponent']
}>()

const backgroundColor = computed(() => {
    return props.centeredText.backgroundColor
})
</script>

<template>
    <div class="centered-text">
        <!-- eslint-disable vue/no-v-html props.centeredText is safe -->
        <h2 class="centered-text__title" :style="{ color: props.centeredText.headlineColor }" v-html="props.centeredText.headline" />
        <div v-if="props.centeredText.richtext" class="centered-text__rich-text">
            <LazyNimbusStrapiBlockRenderer :content="props.centeredText.richtext as StrapiBlock[]" />
        </div>
        <div v-else class="centered-text__text" v-html="props.centeredText.text" />
        <div class="cta-container">
            <UIButton
                v-if="props.centeredText.ctaLink && props.centeredText.ctaText"
                class="centered-text__cta"
                as="cta"
                size="xl"
                :href="props.centeredText.ctaLink"
            >
                <span v-html="props.centeredText.ctaText" />
            </UIButton>
        </div>
        <!-- eslint-enable vue/no-v-html -->
    </div>
</template>

<style scoped lang="scss">
.centered-text {
    width: 100%;
    margin: 0 auto;
    text-align: center;
    word-wrap: break-word;
    line-height: 1.15;
    background-color: v-bind(backgroundColor);

    &__title {
        font-size: 24px;
        font-weight: 800;
        margin-bottom: 20px;

        @include mobile(sm) {
            font-size: 26px;
        }

        @include tablet {
            padding-top: unset;
            font-size: 36px;
        }
    }

    &__text {
        font-size: 18px;
        font-weight: 700;
        word-wrap: inherit;

        @include tablet {
            font-size: 22px;
        }
    }

    &__cta {
        margin-top: 20px;
        width: 100%;

        @include tablet {
            min-width: 400px;
            width: auto;
        }
    }

    &__rich-text {
        :deep(h1),
        :deep(h2),
        :deep(h3) {
            font-weight: 700;
            margin: 15px 0 5px;

            @include tablet {
                font-size: 24px;
            }

            @include desktop-xl {
                font-size: 24px;
            }
        }
    }
}

.cta-container {
    padding: 0 15px;
}
</style>
